import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import api from '@/api/client';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import { AGENT_META, IconCheck } from '@/components/ui/Icons';
import { useTenant } from '@/contexts/TenantContext';

// ---------------------------------------------------------------------------
// SetupWizard — 4-step "finish setting up" modal, opened from the floating
// "+" FAB (SetupFab). Unlike /onboarding this is stateless and re-openable:
// every step is skippable and reflects live completion state fetched from the
// same endpoints the dedicated pages use.
//
//   Step 1  Channel    → POST /api/onboarding/telegram  (validates getMe +
//                        registers the webhook) or PUT /api/channels/whatsapp
//   Step 2  Agents     → GET /api/roles/catalog + GET /api/roles,
//                        POST /api/roles { baseRole } to activate
//   Step 3  Knowledge  → POST /api/knowledge (title=question, content=answer,
//                        category='faq') — same payload as the KB Add Article
//   Step 4  Test       → link out to /test-chat (+ the bot's t.me link when
//                        Telegram was connected in this session)
// ---------------------------------------------------------------------------

interface StatusResponse {
  ok: true;
  hasProfile: boolean;
  hasBot: boolean;
  hasTestMessage: boolean;
  completed: boolean;
  tenant: { name: string; vertical: string };
  bots: Array<{ id: string; channel: string }>;
}

interface TelegramResponse {
  ok: boolean;
  botId?: string;
  botUsername?: string;
  botName?: string;
  telegramLink?: string;
  /** The API validates the token but never fails the step on webhook
   * registration — false means the bot will NOT receive messages yet. */
  webhookRegistered?: boolean;
  reason?: string;
  error?: string;
  detail?: string;
}

/** The API client throws a plain `{message, status}` object (ApiError),
 * never an Error instance — `instanceof Error` is always false for it. */
function errMessage(err: unknown, fallback: string): string {
  const m = (err as { message?: unknown } | null)?.message;
  return typeof m === 'string' && m ? m : fallback;
}

interface CatalogRole {
  name: string;
  displayName: string;
  description: string;
  agentKind: string;
  featureKey: string | null;
}

interface TenantRole {
  id: string;
  name: string;
  active: boolean;
}

interface SetupWizardProps {
  open: boolean;
  onClose: () => void;
}

type StepN = 1 | 2 | 3 | 4;

function SetupWizard({ open, onClose }: SetupWizardProps) {
  const { hasFeature } = useTenant();
  const [step, setStep] = useState<StepN>(1);

  // Live completion state — same GETs the dedicated pages use.
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [catalog, setCatalog] = useState<CatalogRole[]>([]);
  const [tenantRoles, setTenantRoles] = useState<TenantRole[]>([]);

  // Step 1 — channel
  const [channelTab, setChannelTab] = useState<'telegram' | 'whatsapp'>('telegram');
  const [botToken, setBotToken] = useState('');
  const [tgSaving, setTgSaving] = useState(false);
  const [tgResult, setTgResult] = useState<TelegramResponse | null>(null);
  const [tgErr, setTgErr] = useState<string | null>(null);
  const [waPhoneNumberId, setWaPhoneNumberId] = useState('');
  const [waAccessToken, setWaAccessToken] = useState('');
  const [waVerifyToken, setWaVerifyToken] = useState('');
  const [waSaving, setWaSaving] = useState(false);
  const [waSaved, setWaSaved] = useState(false);
  const [waErr, setWaErr] = useState<string | null>(null);

  // Step 2 — agents
  const [agentBusy, setAgentBusy] = useState<string | null>(null);
  const [agentErr, setAgentErr] = useState<string | null>(null);

  // Step 3 — knowledge quick-add
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [kbSaving, setKbSaving] = useState(false);
  const [kbErr, setKbErr] = useState<string | null>(null);
  const [added, setAdded] = useState<string[]>([]);

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get<StatusResponse>('/onboarding/status');
      setStatus(res.data);
    } catch {
      /* best-effort — completion badges just stay blank */
    }
  }, []);

  const loadRoles = useCallback(async () => {
    try {
      const [cRes, tRes] = await Promise.all([
        api.get<{ roles: CatalogRole[] }>('/roles/catalog'),
        api.get<{ roles: TenantRole[] }>('/roles'),
      ]);
      setCatalog(cRes.data.roles ?? []);
      setTenantRoles(tRes.data.roles ?? []);
    } catch {
      setAgentErr('Failed to load the agent catalog.');
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void loadStatus();
    void loadRoles();
  }, [open, loadStatus, loadRoles]);

  const tenantByName = useMemo(() => {
    const map = new Map<string, TenantRole>();
    for (const r of tenantRoles) map.set(r.name, r);
    return map;
  }, [tenantRoles]);

  // Activated = the role row exists AND is switched on (parity with Roles.tsx,
  // which shows role-exists-but-inactive as 'Off', never 'Active').
  const activatedCount = useMemo(
    () => catalog.filter((c) => tenantByName.get(c.name)?.active).length,
    [catalog, tenantByName],
  );

  const connectTelegram = async () => {
    if (!botToken.trim()) {
      setTgErr('Paste the bot token from @BotFather.');
      return;
    }
    setTgSaving(true);
    setTgErr(null);
    try {
      const res = await api.post<TelegramResponse>('/onboarding/telegram', {
        botToken: botToken.trim(),
      });
      if (!res.data.ok) {
        setTgErr(res.data.detail || res.data.error || 'Telegram rejected the token.');
        return;
      }
      setTgResult(res.data);
      setBotToken('');
      await loadStatus();
    } catch (err) {
      setTgErr(errMessage(err, 'Failed to validate the token.'));
    } finally {
      setTgSaving(false);
    }
  };

  const saveWhatsApp = async () => {
    if (!waPhoneNumberId.trim() || !waAccessToken.trim() || !waVerifyToken.trim()) {
      setWaErr('All three fields are required.');
      return;
    }
    setWaSaving(true);
    setWaErr(null);
    setWaSaved(false);
    try {
      await api.put('/channels/whatsapp', {
        config: {
          phoneNumberId: waPhoneNumberId.trim(),
          accessToken: waAccessToken.trim(),
          verifyToken: waVerifyToken.trim(),
        },
        active: true,
      });
      setWaSaved(true);
      await loadStatus();
    } catch (err) {
      setWaErr(errMessage(err, 'Failed to save the credentials.'));
    } finally {
      setWaSaving(false);
    }
  };

  const activateAgent = async (c: CatalogRole) => {
    setAgentBusy(c.name);
    setAgentErr(null);
    try {
      // Existing-but-switched-off role: turn it back on (same PATCH Roles.tsx
      // uses). POST /roles is only for roles that were never activated.
      if (tenantByName.has(c.name)) {
        await api.patch(`/roles/${encodeURIComponent(c.name)}`, { active: true });
      } else {
        await api.post('/roles', { baseRole: c.name });
      }
      await loadRoles();
    } catch (err) {
      const label = AGENT_META[c.agentKind]?.label ?? c.displayName;
      setAgentErr(`Failed to activate ${label}: ${errMessage(err, 'unknown error')}`);
    } finally {
      setAgentBusy(null);
    }
  };

  const addArticle = async () => {
    if (!question.trim() || !answer.trim()) {
      setKbErr('Both the question and the answer are required.');
      return;
    }
    setKbSaving(true);
    setKbErr(null);
    try {
      await api.post('/knowledge', {
        title: question.trim(),
        content: answer.trim(),
        category: 'faq',
        tags: [],
      });
      setAdded((prev) => [...prev, question.trim()]);
      setQuestion('');
      setAnswer('');
    } catch (err) {
      setKbErr(errMessage(err, 'Failed to save the article.'));
    } finally {
      setKbSaving(false);
    }
  };

  const steps: Array<{ n: StepN; title: string; done: boolean }> = [
    { n: 1, title: 'Channel', done: status?.hasBot ?? false },
    { n: 2, title: 'Agents', done: activatedCount > 0 },
    { n: 3, title: 'Knowledge', done: added.length > 0 },
    { n: 4, title: 'Test', done: status?.hasTestMessage ?? false },
  ];

  const next = () => setStep((s) => (s < 4 ? ((s + 1) as StepN) : s));
  const back = () => setStep((s) => (s > 1 ? ((s - 1) as StepN) : s));

  return (
    <Modal open={open} onClose={onClose} title="Set up your workspace" size="lg">
      <div className="space-y-4">
        {/* Step rail — every step is clickable and skippable. */}
        <ol className="flex gap-2">
          {steps.map((s) => (
            <li key={s.n} className="flex-1">
              <button
                onClick={() => setStep(s.n)}
                className={clsx(
                  'w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-colors',
                  step === s.n
                    ? 'border-accent/50 bg-accent/5 text-text'
                    : s.done
                      ? 'border-ok/40 bg-ok/5 text-ok hover:border-ok/60'
                      : 'border-line text-muted hover:border-line-strong hover:text-text',
                )}
              >
                <span
                  className={clsx(
                    'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0',
                    s.done
                      ? 'bg-ok text-white'
                      : step === s.n
                        ? 'bg-accent text-white'
                        : 'bg-panel-soft',
                  )}
                >
                  {s.done ? <IconCheck className="w-3 h-3" /> : s.n}
                </span>
                <span className="truncate">{s.title}</span>
              </button>
            </li>
          ))}
        </ol>

        <div className="max-h-[55vh] overflow-y-auto pr-1">
          {step === 1 && (
            <ChannelStep
              channelTab={channelTab}
              setChannelTab={setChannelTab}
              hasBot={status?.hasBot ?? false}
              botToken={botToken}
              setBotToken={setBotToken}
              tgSaving={tgSaving}
              tgResult={tgResult}
              tgErr={tgErr}
              connectTelegram={connectTelegram}
              waPhoneNumberId={waPhoneNumberId}
              setWaPhoneNumberId={setWaPhoneNumberId}
              waAccessToken={waAccessToken}
              setWaAccessToken={setWaAccessToken}
              waVerifyToken={waVerifyToken}
              setWaVerifyToken={setWaVerifyToken}
              waSaving={waSaving}
              waSaved={waSaved}
              waErr={waErr}
              saveWhatsApp={saveWhatsApp}
            />
          )}

          {step === 2 && (
            <div className="space-y-3 text-sm">
              <p className="text-xs text-muted">
                Turn on the agents this business needs. Each one starts working as soon as
                it&apos;s activated — you can customize or switch them off later from Manage
                agents.
              </p>
              {agentErr && <p className="text-red-400 text-xs">{agentErr}</p>}
              {catalog.length === 0 && !agentErr && (
                <p className="text-xs text-muted">Loading agents…</p>
              )}
              <ul className="space-y-2">
                {catalog.map((c) => {
                  const role = tenantByName.get(c.name);
                  const activated = role?.active === true;
                  const locked = c.featureKey !== null && !hasFeature(c.featureKey);
                  const meta = AGENT_META[c.agentKind];
                  return (
                    <li
                      key={c.name}
                      className={clsx(
                        'flex items-center gap-3 rounded-lg border border-line px-3 py-2.5',
                        locked && 'opacity-60',
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-text truncate">
                          {meta?.label ?? c.displayName}
                        </p>
                        <p className="text-xs text-muted truncate">
                          {meta?.blurb ?? c.description}
                        </p>
                      </div>
                      {activated ? (
                        <span className="flex items-center gap-1.5 text-xs text-ok shrink-0">
                          <IconCheck className="w-3.5 h-3.5" /> Active
                        </span>
                      ) : locked ? (
                        <span className="text-[11px] text-muted shrink-0">Not in plan</span>
                      ) : (
                        <button
                          onClick={() => activateAgent(c)}
                          disabled={agentBusy === c.name}
                          className="text-xs px-3 py-1.5 rounded-md border border-accent/30 text-accent bg-accent/10 hover:bg-accent/20 shrink-0 disabled:opacity-50"
                        >
                          {agentBusy === c.name ? 'Working…' : role ? 'Turn on' : 'Activate'}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3 text-sm">
              <p className="text-xs text-muted">
                Teach the bot the questions customers actually ask. Add a few now — each
                pair is saved as an FAQ article the agents can quote.
              </p>
              <Field label="Question">
                <input
                  type="text"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="e.g. Do you deliver on Fridays?"
                  className="glass-input w-full"
                  maxLength={200}
                />
              </Field>
              <Field label="Answer">
                <textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  rows={3}
                  placeholder="e.g. Yes — Friday delivery runs 3pm to 11pm."
                  className="glass-input w-full"
                />
              </Field>
              {kbErr && <p className="text-red-400 text-xs">{kbErr}</p>}
              <div className="flex items-center justify-between gap-2">
                <Link
                  to="/knowledge"
                  onClick={onClose}
                  className="text-xs text-accent hover:underline"
                >
                  Need to upload files? Open Knowledge →
                </Link>
                <Button size="sm" onClick={addArticle} isLoading={kbSaving} disabled={kbSaving}>
                  Add article
                </Button>
              </div>
              {added.length > 0 && (
                <ul className="space-y-1.5 pt-1">
                  {added.map((q, i) => (
                    <li
                      key={`${q}-${i}`}
                      className="flex items-center gap-2 rounded-lg border border-ok/30 bg-ok/5 px-3 py-2 text-xs text-text"
                    >
                      <IconCheck className="w-3.5 h-3.5 text-ok shrink-0" />
                      <span className="truncate">{q}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3 text-sm">
              <p className="text-xs text-muted">
                Make sure everything works before customers do. Test Chat talks to the same
                brain your channels use — no bot required.
              </p>
              <Link
                to="/test-chat"
                onClick={onClose}
                className="block rounded-lg border border-accent/30 bg-accent/5 px-3 py-3 text-sm text-accent hover:bg-accent/10 transition-colors"
              >
                Open Test Chat →
              </Link>
              {tgResult?.telegramLink && (
                <a
                  href={tgResult.telegramLink}
                  target="_blank"
                  rel="noreferrer"
                  className="block rounded-lg border border-line px-3 py-3 text-sm text-text hover:bg-panel-soft transition-colors"
                >
                  Or message your bot directly: {tgResult.telegramLink} →
                </a>
              )}
              <div
                className={clsx(
                  'rounded-lg border px-3 py-3 text-xs flex items-center gap-2',
                  status?.hasTestMessage
                    ? 'border-ok/40 bg-ok/5 text-ok'
                    : 'border-line bg-panel-soft/40 text-muted',
                )}
              >
                <span
                  className={clsx(
                    'w-2 h-2 rounded-full',
                    status?.hasTestMessage ? 'bg-ok' : 'bg-muted',
                  )}
                />
                <span>
                  {status?.hasTestMessage
                    ? 'A test message already came through — you’re live.'
                    : 'No test message received yet.'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer — Back / Skip / Next. Every step can be skipped. */}
        <div className="flex items-center justify-between pt-3 border-t border-line">
          <button
            onClick={back}
            className={clsx('text-xs text-muted hover:text-text', step === 1 && 'invisible')}
          >
            ← Back
          </button>
          <div className="flex gap-2">
            {step < 4 ? (
              <>
                <Button variant="ghost" size="sm" onClick={next}>
                  Skip
                </Button>
                <Button size="sm" onClick={next}>
                  Next
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={onClose}>
                Done
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — channel connect (Telegram / WhatsApp tabs)
// ---------------------------------------------------------------------------

interface ChannelStepProps {
  channelTab: 'telegram' | 'whatsapp';
  setChannelTab: (t: 'telegram' | 'whatsapp') => void;
  hasBot: boolean;
  botToken: string;
  setBotToken: (v: string) => void;
  tgSaving: boolean;
  tgResult: TelegramResponse | null;
  tgErr: string | null;
  connectTelegram: () => void;
  waPhoneNumberId: string;
  setWaPhoneNumberId: (v: string) => void;
  waAccessToken: string;
  setWaAccessToken: (v: string) => void;
  waVerifyToken: string;
  setWaVerifyToken: (v: string) => void;
  waSaving: boolean;
  waSaved: boolean;
  waErr: string | null;
  saveWhatsApp: () => void;
}

function ChannelStep({
  channelTab,
  setChannelTab,
  hasBot,
  botToken,
  setBotToken,
  tgSaving,
  tgResult,
  tgErr,
  connectTelegram,
  waPhoneNumberId,
  setWaPhoneNumberId,
  waAccessToken,
  setWaAccessToken,
  waVerifyToken,
  setWaVerifyToken,
  waSaving,
  waSaved,
  waErr,
  saveWhatsApp,
}: ChannelStepProps) {
  return (
    <div className="space-y-4 text-sm">
      <div className="flex gap-2">
        {(['telegram', 'whatsapp'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setChannelTab(t)}
            className={clsx(
              'px-3 py-1.5 rounded-md text-xs border transition-colors',
              channelTab === t
                ? 'border-accent/50 bg-accent/10 text-accent'
                : 'border-line text-muted hover:text-text hover:bg-panel-soft',
            )}
          >
            {t === 'telegram' ? 'Telegram' : 'WhatsApp'}
          </button>
        ))}
      </div>

      {channelTab === 'telegram' && (
        <div className="space-y-3">
          {hasBot && !tgResult && (
            <div className="rounded-lg border border-ok/30 bg-ok/5 px-3 py-2 text-xs text-text">
              A bot is already connected. You can connect another one below.
            </div>
          )}
          <ol className="space-y-2 text-xs text-muted list-decimal pl-4">
            <li>
              Open Telegram, message{' '}
              <a
                href="https://t.me/BotFather"
                target="_blank"
                rel="noreferrer"
                className="text-accent hover:underline"
              >
                @BotFather
              </a>{' '}
              and send <code className="bg-panel-soft px-1 rounded">/newbot</code>.
            </li>
            <li>
              Pick a display name and a username ending in &quot;bot&quot; (e.g.{' '}
              <code className="bg-panel-soft px-1 rounded">shinylaundry_bot</code>).
            </li>
            <li>BotFather replies with an API token — paste it below.</li>
          </ol>
          <Field label="Bot token">
            <input
              type="text"
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              placeholder="123456789:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
              className="glass-input w-full font-mono text-xs"
            />
          </Field>
          {tgErr && <p className="text-red-400 text-xs">{tgErr}</p>}
          {tgResult?.botUsername && tgResult.webhookRegistered === false && (
            <div className="rounded-lg border border-warn/40 bg-warn/5 px-3 py-2 text-xs text-warn">
              Token validated, but webhook registration failed
              {tgResult.reason ? ` (${tgResult.reason})` : ''} — the bot will not
              receive messages yet. Fix the server&apos;s public URL and reconnect.
            </div>
          )}
          {tgResult?.botUsername && (
            <div className="rounded-lg border border-ok/30 bg-ok/5 px-3 py-2 text-xs">
              Bot connected:{' '}
              <span className="font-medium text-text">@{tgResult.botUsername}</span>
              {tgResult.telegramLink && (
                <>
                  {' — '}
                  <a
                    href={tgResult.telegramLink}
                    target="_blank"
                    rel="noreferrer"
                    className="text-accent hover:underline"
                  >
                    open {tgResult.telegramLink}
                  </a>
                </>
              )}
            </div>
          )}
          <div className="flex justify-end">
            <Button size="sm" onClick={connectTelegram} isLoading={tgSaving} disabled={tgSaving}>
              Validate &amp; connect
            </Button>
          </div>
        </div>
      )}

      {channelTab === 'whatsapp' && (
        <div className="space-y-3">
          <div className="rounded-lg border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-text">
            <strong>Heads up:</strong> WhatsApp is not a day-1 channel. Saving credentials
            here is only half the job — Meta Business verification takes days, and the
            webhook must be wired manually in Meta Business Manager. Connect Telegram
            first; treat WhatsApp as a follow-up project.
          </div>
          <Field label="Phone Number ID">
            <input
              type="text"
              value={waPhoneNumberId}
              onChange={(e) => setWaPhoneNumberId(e.target.value)}
              placeholder="e.g. 104857623456789"
              className="glass-input w-full font-mono text-xs"
            />
          </Field>
          <Field label="Access Token">
            <input
              type="text"
              value={waAccessToken}
              onChange={(e) => setWaAccessToken(e.target.value)}
              placeholder="EAAG…"
              className="glass-input w-full font-mono text-xs"
            />
          </Field>
          <Field label="Verify Token">
            <input
              type="text"
              value={waVerifyToken}
              onChange={(e) => setWaVerifyToken(e.target.value)}
              placeholder="Any secret string — must match Meta's webhook config"
              className="glass-input w-full font-mono text-xs"
            />
          </Field>
          {waErr && <p className="text-red-400 text-xs">{waErr}</p>}
          {waSaved && (
            <div className="rounded-lg border border-ok/30 bg-ok/5 px-3 py-2 text-xs text-text">
              Credentials saved. Finish the webhook setup in Meta Business Manager to go
              live.
            </div>
          )}
          <div className="flex justify-end">
            <Button size="sm" onClick={saveWhatsApp} isLoading={waSaving} disabled={waSaving}>
              Save credentials
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1">{label}</label>
      {children}
    </div>
  );
}

export default SetupWizard;
